import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useEvent,
    useListen,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { getHouseSelectorView } from '@features/properties/store';
import { getHouseSelectorHelperData } from '@features/properties/utilities/data';
import { Dimensions, ScreenCoordinates } from '@features/virtual-tour/types';
import {
    useMousePosition,
    usePanningImage,
    useResizableViewport,
} from '@features/virtual-tour/utilities/hooks';
import { getDebugState } from '@store/debugger';

import { houseSelectorEvents } from './constants';
import { applyStyle, initCanvasStyles } from './utilities/canvas-styles';
import { convertPercentageGeometryToPixels } from './utilities/convert-percentage-to-pixel';
import { drawPolygon } from './utilities/draw-polygon';
import { findPolygonCenterPoint } from './utilities/house-selector-data';
import { renderPointer } from './utilities/render-pointer';
import {
    HouseSelectorObject,
    HouseSelectorObjectHandler,
    HouseSelectorPointer,
    HouseSelectorPolygon,
    HouseSelectorView,
} from './utilities/types';
import { useCanvasRepaint } from './utilities/use-canvas-repaint';

const POLYGON_HOVER_CLASS = 'house-selector--polygon-hover';

export interface HouseSelectorElement extends Component {
    highlightedPolygons: HouseSelectorPolygon[] | null;
    hoverDisabled: boolean;
    currentView: HouseSelectorView | null;
    setView: (view: HouseSelectorView) => void;
}

const HouseSelector: FC<HouseSelectorElement> = ({ element }) => {
    const [, setHighlightedPolygons, watchHighlightedPolygons] = useProp<
        HouseSelectorPolygon[] | null
    >('highlightedPolygons', null);
    const [, setCurrentView] = useProp<HouseSelectorView | null>('currentView', null);
    const [onObjectClick] = useProp<HouseSelectorObjectHandler>('onObjectClick');
    useProp<boolean>('hoverDisabled', false);

    const houseSelectorCanvas = useElement<HTMLCanvasElement>('[js-hook-house-selector-canvas]');
    const viewport = useElement<HTMLElement>('[js-hook-house-selector-viewport]');
    const imageContainer = useElement<HTMLElement>('[js-hook-house-selector-view-image]');
    const pointersContainer = useElement<HTMLElement>('[js-hook-house-selector-pointers]');
    const polygonIdHelper = useElement<HTMLElement>('[js-hook-house-selector-polygon-id-helper]');

    let houseSelectorCanvasContext: CanvasRenderingContext2D;

    const panningImage = usePanningImage({
        imageContainer,
        viewport,
        onResize: setCanvasDimensions,
    });

    const { refresh, dimensions: viewportDimensions } = useResizableViewport({
        viewport,
        onResize: panningImage.resize,
    });

    const { updatePosition } = useMousePosition({
        viewport: houseSelectorCanvas,
        onUpdate: ({ position }) => position && handleHoverPolygon(position),
    });

    const canvasRepaintState = useCanvasRepaint({ onRepaint: drawViewObjects });

    const polygonHoverEvent = useEvent({ eventName: houseSelectorEvents.POLYGON_HOVER });

    const { showIdInHouseSelector } = getDebugState();

    onDidLoad(async () => {
        panningImage.setInteractionBehavior('scroll');
        const canvasContext = houseSelectorCanvas.current?.getContext('2d');

        if (!canvasContext) {
            throw new Error('[house-selector] -- ERROR: could not initialize canvas context');
        }

        houseSelectorCanvasContext = canvasContext;
        const canvasStyles = await initCanvasStyles(houseSelectorCanvasContext);
        canvasRepaintState.setState({ styles: canvasStyles });
    });

    useListen(window, 'resize', refresh);
    useListen(window, 'scroll', refresh);
    useListen(document, 'properties-view::map-resized', refresh);
    useListen(houseSelectorCanvas, 'mousemove', updateMousePosition);

    useBindMethod('setView', setView);

    useListen(houseSelectorCanvas, 'click', async () => {
        const { highlightedPolygons } = element;
        if (!highlightedPolygons?.length) return;

        const { objectType, linkToViewId } = highlightedPolygons[0];

        if (objectType === 'viewLink' && linkToViewId) {
            const view = getHouseSelectorView(linkToViewId);
            view && setView(view);
            return;
        }

        onObjectClick(highlightedPolygons[0]);
    });

    watchHighlightedPolygons(async (nextHighlighted, prevHighlighted) => {
        if (prevHighlighted?.length && !element.hoverDisabled) {
            prevHighlighted
                .filter(({ objectId }) => !!objectId)
                .forEach(({ objectId }) => polygonHoverEvent.emit({ id: objectId, active: false }));
        }

        canvasRepaintState.setState({ hoverPolygons: nextHighlighted });
        element.classList[!!nextHighlighted ? 'add' : 'remove'](POLYGON_HOVER_CLASS);

        if (nextHighlighted?.length && !element.hoverDisabled) {
            nextHighlighted
                .filter(({ objectId }) => !!objectId)
                .forEach(({ objectId }) => polygonHoverEvent.emit({ id: objectId, active: true }));
        } else if (nextHighlighted?.length && element.hoverDisabled) {
            const focusPoint = findPolygonCenterPoint(nextHighlighted[0], {
                asPercentage: true,
            }) as ScreenCoordinates;

            focusPoint && panningImage.focusPoint(focusPoint);
        }
    });

    async function handleHoverPolygon(position: ScreenCoordinates) {
        const hoveredPolygon = getCurrentHoveredPolygon(position);

        if (
            element.highlightedPolygons?.length &&
            element.highlightedPolygons[0] === hoveredPolygon
        ) {
            return;
        }

        setHighlightedPolygons(hoveredPolygon ? [hoveredPolygon] : null);
    }

    function updateMousePosition(e: MouseEvent) {
        if (!houseSelectorCanvas.current) return;
        const { x, y } = houseSelectorCanvas.current.getBoundingClientRect();
        const { width, height } = houseSelectorCanvas.current;
        updatePosition(e, { width, height }, { x, y });
    }

    function drawViewObjects() {
        const { polygons, pointers } = element.currentView || {};

        if (polygons?.length) {
            const { styles, dimensions, hoverPolygons } = canvasRepaintState.getState();
            convertPercentageGeometryToPixels(dimensions!, polygons);

            houseSelectorCanvasContext.clearRect(0, 0, dimensions!.width, dimensions!.height);
            polygons.forEach(async (object: HouseSelectorPolygon) => {
                const hovering = hoverPolygons?.some((hovered) => hovered === object);

                if (object.hidden || !object.variant || !styles) return;
                let style = styles.polygons[object.variant];

                if (object.canHover && hovering) style = styles.polygons['highlighted'];

                applyStyle(houseSelectorCanvasContext, style);
                drawPolygon(houseSelectorCanvasContext, object);
            });
        }

        if (pointersContainer.current) {
            pointersContainer.current.innerHTML = '';
            if (!pointers?.length) return;

            pointersContainer.current.append(
                ...pointers.map((pointer) =>
                    renderPointer(pointer, { onPointerClick: handlePointerClick }),
                ),
            );
        }
    }

    function setCanvasDimensions(dimensions: Dimensions) {
        houseSelectorCanvas.current!.width = dimensions.width;
        houseSelectorCanvas.current!.height = dimensions.height;

        window.requestAnimationFrame(async () => {
            if (!(dimensions.width && dimensions.height)) return;
            canvasRepaintState.setState({ dimensions });
        });
    }

    async function setView(view: HouseSelectorView) {
        if (element.currentView?.id !== view.id) {
            setCurrentView(view);
            canvasRepaintState.setState({ dimensions: null });
            await panningImage.setSource(view.imageUrl!);
            panningImage.resize(viewportDimensions);
        }

        canvasRepaintState.requestRepaint();
    }

    function getCurrentHoveredPolygon({ x, y }: ScreenCoordinates): HouseSelectorPolygon | null {
        if (!element.currentView?.polygons?.length) return null;

        const hoveredPolygons = element.currentView.polygons
            .filter(({ path }) => path && houseSelectorCanvasContext.isPointInPath(path, x, y))
            .reverse();

        const activeHoveredPolygons = hoveredPolygons.filter((polygon) => polygon.canHover);

        if (showIdInHouseSelector) {
            if (activeHoveredPolygons[0]) showHelper(activeHoveredPolygons[0]);
            else if (hoveredPolygons[0]) showHelper(hoveredPolygons[0]);
        }

        return activeHoveredPolygons[0] ?? null;
    }

    function handlePointerClick(pointer: HouseSelectorPointer) {
        if (!pointer.linkToViewId) return;
        const view = getHouseSelectorView(pointer.linkToViewId);
        view && setView(view);
    }

    function showHelper(object: HouseSelectorObject) {
        if (!polygonIdHelper.current) return;
        polygonIdHelper.current.innerHTML = '';

        polygonIdHelper.current.append(<pre>{getHouseSelectorHelperData(object)}</pre>);
    }

    return (
        <Fragment>
            <div class="house-selector__viewport" js-hook-house-selector-viewport>
                <div class="house-selector__view-image" js-hook-house-selector-view-image />

                <canvas
                    width="100%"
                    height="100%"
                    id="houseSelectorCanvas"
                    class="house-selector__canvas"
                    js-hook-house-selector-canvas>
                    <p>Something went wrong</p>
                </canvas>

                <div class="house-selector__pointers" js-hook-house-selector-pointers />
            </div>

            {showIdInHouseSelector ? (
                <div
                    class="house-selector__polygon-id-helper"
                    js-hook-house-selector-polygon-id-helper
                />
            ) : null}
        </Fragment>
    );
};

HouseSelector.props = {
    highlightedPolygons: {
        type: Object,
    },
    hoverDisabled: {
        type: Object,
    },
    currentView: {
        type: Object,
    },
    onObjectClick: {
        type: Function,
        required: true,
    },
};

defineElement('bpd-house-selector', HouseSelector);
